import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { TrialProvider, useTrial, TrialExpired } from "@/components/TrialGuard";
import { OfflineProvider } from "@/context/OfflineContext";
import AuthCallback from "@/pages/AuthCallback";
import FAQ from "@/pages/FAQ";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import Disclaimer from "@/pages/legal/Disclaimer";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import CookieConsent from "@/components/CookieConsent";
import UpdateToast from "@/components/UpdateToast";
import Contact from "@/pages/Contact";
import BookDemo from "@/pages/BookDemo";
import Features from "@/pages/Features";
import Enquiries from "@/pages/Enquiries";
import AdminDataEditor from "@/pages/AdminDataEditor";
import Careers from "@/pages/Careers";
import Blog, { BlogPost } from "@/pages/Blog";
import IndustryPage from "@/pages/industry/IndustryPage";
import Home from "@/pages/Home";
import Pricing from "@/pages/Pricing";
import DemoPage from "@/pages/demo/DemoPage";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SuperAdmin from "@/pages/SuperAdmin";
import AdminLeads from "@/pages/AdminLeads";
import Settings from "@/pages/Settings";
import SharedReport from "@/pages/SharedReport";
import OmLayout from "@/pages/om/OmLayout";
import OmDashboard from "@/pages/om/OmDashboard";
import OmAssets from "@/pages/om/OmAssets";
import OmDailyLog from "@/pages/om/OmDailyLog";
import OmPreventive from "@/pages/om/OmPreventive";
import OmCorrective from "@/pages/om/OmCorrective";
import OmInventory from "@/pages/om/OmInventory";
import OmReports from "@/pages/om/OmReports";
import OmScan from "@/pages/om/OmScan";
import OmLanding from "@/pages/om/OmLanding";
import OmReliability from "@/pages/om/OmReliability";
import OmRcaLibrary from "@/pages/om/OmRcaLibrary";
import OmRcaDetail from "@/pages/om/OmRcaDetail";
import AdminRcaTemplates from "@/pages/AdminRcaTemplates";
import OmSectors from "@/pages/om/OmSectors";
import OmSectorPage from "@/pages/om/OmSectorPage";
import About from "@/pages/About";
import Billing from "@/pages/Billing";
import Projects from "@/pages/Projects";
import Dashboard from "@/pages/Dashboard";
import WBS from "@/pages/WBS";
import RiskRadar from "@/pages/RiskRadar";
import BOQParser from "@/pages/BOQParser";
import DPR from "@/pages/DPR";
import Planning from "@/pages/Planning";
import CPM from "@/pages/CPM";
import Hindrances from "@/pages/Hindrances";
import Resources from "@/pages/Resources";
import Costing from "@/pages/Costing";
import WhatIf from "@/pages/WhatIf";
import Store from "@/pages/Store";
import KPIPage from "@/pages/KPIPage";
import Reports from "@/pages/Reports";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading workspace…</div>
);

/** Hard gate: no session, no app. Expired trial sees only the upgrade wall. */
const Guard = ({ children, allowLocked = false, superOnly = false }) => {
  const { user } = useAuth();
  const { status } = useTrial();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login?reason=auth" replace />;
  if (superOnly && user.role !== "superadmin") return <Navigate to="/projects" replace />;
  if (!allowLocked) {
    if (status === null) return <Loading />;
    if (status && status.locked) return <TrialExpired />;
  }
  return children;
};

const Public = ({ children }) => {
  const { user } = useAuth();
  if (user && user !== false) return <Navigate to={user.role === "superadmin" ? "/admin" : "/projects"} replace />;
  return children;
};

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <TrialProvider>
            <OfflineProvider>
            <Toaster position="top-right" theme="system" />
            <CookieConsent />
            <UpdateToast />
            <AppRoutes />
            </OfflineProvider>
          </TrialProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Emergent OAuth returns to {origin}/projects#session_id=… — handle it before any guard runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Public><Login /></Public>} />
              <Route path="/signup" element={<Public><Signup /></Public>} />
              <Route path="/forgot-password" element={<Public><ForgotPassword /></Public>} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/admin" element={<Guard allowLocked superOnly><SuperAdmin /></Guard>} />
              <Route path="/om" element={<OmLanding />} />
              <Route path="/om/sectors" element={<OmSectors />} />
              <Route path="/om/sectors/:slug" element={<OmSectorPage />} />
              <Route path="/om" element={<Guard allowLocked><OmLayout /></Guard>}>
                <Route path="dashboard" element={<OmDashboard />} />
                <Route path="assets" element={<OmAssets />} />
                <Route path="daily-log" element={<OmDailyLog />} />
                <Route path="preventive-maintenance" element={<OmPreventive />} />
                <Route path="corrective-maintenance" element={<OmCorrective />} />
                <Route path="inventory" element={<OmInventory />} />
                <Route path="reports" element={<OmReports />} />
                <Route path="reliability" element={<OmReliability />} />
                <Route path="rca" element={<OmRcaLibrary />} />
                <Route path="rca/:rcaId" element={<OmRcaDetail />} />
              </Route>
              <Route path="/om/scan/:assetId" element={<Guard allowLocked><OmScan /></Guard>} />
              <Route path="/settings" element={<Guard allowLocked><Settings /></Guard>} />
              <Route path="/shared/:token" element={<Guard allowLocked><SharedReport /></Guard>} />
              <Route path="/about" element={<About />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/features" element={<Features />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/demo" element={<BookDemo />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/demo/:slug" element={<DemoPage />} />
              <Route path="/plans" element={<Guard allowLocked><Billing /></Guard>} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/industry/:slug" element={<IndustryPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/projects" element={<Guard><Projects /></Guard>} />
              <Route path="/pricing" element={<Guard allowLocked><Billing /></Guard>} />
              <Route path="/enquiries" element={<Guard allowLocked><Enquiries /></Guard>} />
              <Route path="/admin/leads" element={<Guard allowLocked superOnly><AdminLeads /></Guard>} />
              <Route path="/admin/rca-templates" element={<Guard allowLocked superOnly><AdminRcaTemplates /></Guard>} />
              <Route path="/admin/data-editor" element={<Guard allowLocked superOnly><AdminDataEditor /></Guard>} />
              <Route path="/billing" element={<Guard allowLocked><Billing /></Guard>} />
              <Route path="/p/:pid" element={<Guard><Layout /></Guard>}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="wbs" element={<WBS />} />
                <Route path="risk" element={<RiskRadar />} />
                <Route path="boq" element={<BOQParser />} />
                <Route path="dpr" element={<DPR />} />
                <Route path="planning" element={<Planning />} />
                <Route path="cpm" element={<CPM />} />
                <Route path="hindrances" element={<Hindrances />} />
                <Route path="resources" element={<Resources />} />
                <Route path="costing" element={<Costing />} />
                <Route path="whatif" element={<WhatIf />} />
                <Route path="store" element={<Store />} />
                <Route path="kpi" element={<KPIPage />} />
                <Route path="reports" element={<Reports />} />
              </Route>
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
  );
}
